import React from 'react';
import { MapPin } from '@phosphor-icons/react';
import { Autocomplete, AutocompleteHandle } from '../Autocomplete';
// Direct import (not the barrel) so bundlers don't pull MobileBottomNav/MobileMenuModal, which
// other files only import dynamically for code-splitting, into whatever chunk this static import
// lands in. See components/navigation/index.ts.
import { Logo } from '../navigation/Logo';
import type { IntlShape } from 'react-intl';

export interface SearchNavbarProps {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
  className?: string;
  /** Opens the location filter modal (remoteness + location search) from the button attached to
   * the search bar's right edge: this is the only place that modal opens from now that the
   * standalone category-button row is gone. */
  onLocationClick?: () => void;
  isLocationActive?: boolean;
  locationHasFilters?: boolean;
  locationHasExcluded?: boolean;
}

/**
 * Search navbar component - contains logo and search bar
 * Used in DesktopFilterCategories for the left side of the search page header
 */
export const SearchNavbar: React.FC<SearchNavbarProps> = ({
  intl,
  formatMessage,
  clearSearch,
  autocompleteRef,
  onAutocompleteOpen,
  className = '',
  onLocationClick,
  isLocationActive = false,
  locationHasFilters = false,
  locationHasExcluded = false,
}) => {
  return (
    <div className={`flex items-center gap-3 min-w-0 flex-1 ${className}`}>
      {/* Logo */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Logo clearSearch={clearSearch} variant="horizontal" locale={intl.locale} />
        <Logo clearSearch={clearSearch} variant="compact" locale={intl.locale} />
      </div>
      {/* Searchbar: no max-width, fills whatever space the row gives it (the old category-button
          row that used to eat this space is gone). The location picker is fused onto its right
          edge (Autocomplete's own right corners squared via `attached`, no gap between the two,
          the button's left border dropped) so the pair reads as one continuous search control
          rather than two separate nav items. */}
      <div className="min-w-0 flex-1 w-full flex items-center">
        <div className="min-w-0 flex-1">
          <Autocomplete
            ref={autocompleteRef}
            onOpen={onAutocompleteOpen}
            attached={!!onLocationClick}
            placeholder={formatMessage({
              id: 'instantsearch.searchBoxLink.placeholder',
              description: 'Placeholder for the search box',
              defaultMessage: 'Search for jobs',
            })}
          />
        </div>
        {onLocationClick && (
          <button
            type="button"
            onClick={onLocationClick}
            className={`flex items-center justify-center gap-1.5 h-[52px] min-w-[52px] shrink-0 px-4 rounded-l-none rounded-r-full text-sm font-medium border-2 border-l-0 transition-all duration-200 cursor-pointer ${
              locationHasExcluded
                ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
                : isLocationActive || locationHasFilters
                  ? 'bg-[#C8DDCE] dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8] border-[#3B5E45]/45 dark:border-[#98CFA8]/35'
                  : 'bg-white/90 dark:bg-[#1F1509]/90 text-[#1E1A14] dark:text-[#F2EADF] border-[#DDD5C8]/70 dark:border-[#4A3520]/80'
            }`}
            aria-label={formatMessage({
              id: 'search.refinements.location',
              defaultMessage: 'Location',
              description: 'Filter label for location selection in search refinements',
            })}
            aria-expanded={isLocationActive}
          >
            <MapPin weight="fill" className="w-4 h-4 shrink-0" />
            <span className="hidden xl:inline whitespace-nowrap">
              {formatMessage({
                id: 'search.refinements.location',
                defaultMessage: 'Location',
                description: 'Filter label for location selection in search refinements',
              })}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
